import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ceoApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  blue:   '#3B82F6',
  green:  '#10B981',
  yellow: '#F59E0B',
  red:    '#EF4444',
  purple: '#8B5CF6',
  teal:   '#14B8A6',
  orange: '#F97316',
  gray:   '#6B7280',
  indigo: '#6366F1',
  pink:   '#EC4899',
};
const PALETTE = [C.blue, C.green, C.yellow, C.red, C.purple, C.teal, C.orange, C.gray, C.indigo, C.pink];

// ── Currency formatter ────────────────────────────────────────────────────────
const fmtCr = (n) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
};

// ── Reusable primitives ───────────────────────────────────────────────────────
function KpiCard({ title, value, sub, color = C.blue, icon, badge, badgeColor }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.4rem',
      border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column', gap: '0.3rem', position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.4 }}>{title}</span>
        <span style={{ fontSize: '1.35rem', lineHeight: 1 }}>{icon}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '2rem', fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</span>
        {badge !== undefined && (
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: badgeColor || C.green }}>
            {badge}
          </span>
        )}
      </div>
      {sub && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: 0, lineHeight: 1.4 }}>{sub}</p>}
      <div style={{ height: '3px', background: `linear-gradient(90deg, ${color}, transparent)`, borderRadius: 9999, marginTop: '0.2rem' }} />
    </div>
  );
}

function SectionHeader({ icon, title, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
      <span style={{ fontSize: '1.25rem' }}>{icon}</span>
      <div>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: theme.text, margin: 0 }}>{title}</h2>
        {sub && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: 0 }}>{sub}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, sub, children, height = 240, span }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.4rem',
      border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      gridColumn: span ? `span ${span}` : undefined,
    }}>
      <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 0.25rem' }}>{title}</p>
      {sub && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0 0 0.75rem' }}>{sub}</p>}
      <div style={{ height }}>{children}</div>
    </div>
  );
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '0.25rem 0' }}>
      <div style={{ flex: 1, height: 1, background: theme.border }} />
      <span style={{ fontSize: '0.7rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: theme.border }} />
    </div>
  );
}

function AlertBadge({ count, color }) {
  if (!count) return null;
  return (
    <span style={{ background: color, color: '#fff', fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.45rem', borderRadius: 999, marginLeft: '0.5rem' }}>
      {count}
    </span>
  );
}

function AlertItem({ alert }) {
  const styles = {
    high:   { bg: '#FEF2F2', border: '#FCA5A5', dot: C.red },
    medium: { bg: '#FFFBEB', border: '#FCD34D', dot: C.yellow },
    green:  { bg: '#F0FDF4', border: '#86EFAC', dot: C.green },
  };
  const s = styles[alert.level] || styles.medium;
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '0.6rem', padding: '0.65rem 0.9rem', display: 'flex', gap: '0.65rem', alignItems: 'flex-start' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0, marginTop: 4 }} />
      <div>
        <p style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text, margin: 0 }}>{alert.title}</p>
        <p style={{ fontSize: '0.73rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>{alert.desc}</p>
      </div>
    </div>
  );
}

function EmptyState({ icon = '📭', text }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '0.5rem', color: theme.textMuted }}>
      <span style={{ fontSize: '1.75rem' }}>{icon}</span>
      <p style={{ fontSize: '0.8rem', margin: 0 }}>{text}</p>
    </div>
  );
}

const TOOLTIP_STYLE = { fontSize: 12, borderRadius: '0.5rem', border: `1px solid ${theme.border}` };
const TICK_STYLE = { fontSize: 11, fill: theme.textMuted };

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CEODashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeSection, setActiveSection] = useState('all');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await ceoApi.getDashboard();
      setData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: '1rem' }}>
      <div style={{ width: 48, height: 48, border: `4px solid ${theme.border}`, borderTop: `4px solid ${C.blue}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>Loading executive dashboard…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '3rem', textAlign: 'center' }}>
      <p style={{ color: C.red, fontWeight: 600, marginBottom: '1rem' }}>{error}</p>
      <button onClick={load} style={{ padding: '0.6rem 1.5rem', background: C.blue, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
    </div>
  );

  if (!data) return null;

  const { employees, attendance_today, attendance_trend, projects, project_trend,
          assets, tickets, sales, claims, recruitment, performance, alerts, leave_wfh } = data;

  const highAlerts   = alerts.filter(a => a.level === 'high');
  const mediumAlerts = alerts.filter(a => a.level === 'medium');
  const greenAlerts  = alerts.filter(a => a.level === 'green');

  // Attendance today pie
  const attPie = [
    { name: 'Present', value: attendance_today.present, color: C.green },
    { name: 'WFH',     value: attendance_today.wfh,     color: C.blue },
    { name: 'On Leave',value: attendance_today.on_leave, color: C.yellow },
    { name: 'Absent',  value: attendance_today.absent,   color: C.red },
  ].filter(d => d.value > 0);

  // Projects pie
  const projPie = [
    { name: 'On Track',  value: projects.on_track,  color: C.green },
    { name: 'Delayed',   value: projects.delayed,   color: C.red },
    { name: 'On Hold',   value: projects.on_hold,   color: C.yellow },
    { name: 'Completed', value: projects.completed, color: C.blue },
  ].filter(d => d.value > 0);

  // Assets pie
  const assetPie = [
    { name: 'Assigned',  value: assets.assigned,  color: C.blue },
    { name: 'Available', value: assets.available, color: C.green },
    { name: 'In Repair', value: assets.in_repair, color: C.yellow },
    { name: 'Retired',   value: assets.retired,   color: C.gray },
  ].filter(d => d.value > 0);

  // Claims pie
  const claimsPie = [
    { name: 'Submitted',  value: claims.submitted, color: C.blue },
    { name: 'Approved',   value: claims.approved,  color: C.green },
    { name: 'Paid',       value: claims.paid,      color: C.teal },
    { name: 'Rejected',   value: claims.rejected,  color: C.red },
  ].filter(d => d.value > 0);

  // Sales stage bar data (ordered)
  const stageOrder = ['identified','under_evaluation','in_progress','submitted','under_review','clarification_requested','won','lost'];
  const stageLabels = { identified:'Identified', under_evaluation:'Evaluation', in_progress:'In Progress', submitted:'Submitted', under_review:'Under Review', clarification_requested:'Clarification', won:'Won', lost:'Lost' };
  const stageData = stageOrder
    .filter(s => sales.stage_counts[s] > 0)
    .map(s => ({ name: stageLabels[s] || s, count: sales.stage_counts[s] }));

  const isCTO = user?.role === 'cto';
  const isCFO = user?.role === 'cfo';
  const roleLabel = isCTO ? 'CTO' : isCFO ? 'CFO' : 'CEO';

  const NAV_TABS = ['all','workforce','sales','projects','it', ...(isCTO ? [] : ['claims']), 'recruitment','performance'];
  const show = (s) => {
    if (isCTO && s === 'claims') return false;
    return activeSection === 'all' || activeSection === s;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #1E40AF 100%)',
        borderRadius: '1rem', padding: '1.75rem 2rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
      }}>
        <div>
          <p style={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 0.3rem' }}>Executive View</p>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#F8FAFC', margin: 0, letterSpacing: '-0.02em' }}>{roleLabel} Dashboard</h1>
          <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: '0.3rem 0 0' }}>
            Welcome, {user?.full_name || roleLabel} · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
          <button onClick={load} style={{ padding: '0.6rem 1.25rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#F8FAFC', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
            ↻ Refresh
          </button>
          {lastRefresh && <span style={{ color: '#64748B', fontSize: '0.72rem' }}>Updated {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
        </div>
      </div>

      {/* ── Section Nav ── */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        {NAV_TABS.map(tab => (
          <button key={tab} onClick={() => setActiveSection(tab)}
            style={{
              padding: '0.35rem 0.9rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600,
              border: `1.5px solid ${activeSection === tab ? C.blue : theme.border}`,
              background: activeSection === tab ? C.blue : '#fff',
              color: activeSection === tab ? '#fff' : theme.textMuted,
              cursor: 'pointer', textTransform: 'capitalize', transition: 'all 0.15s',
            }}>
            {tab === 'all' ? '⊞ All' : tab === 'it' ? '🖥 IT' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── 1. Company Snapshot KPIs ── */}
      <div>
        <SectionHeader icon="📊" title="Company Snapshot" sub="Key metrics across the organisation right now" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '1rem' }}>
          <KpiCard title="Total Headcount"   value={employees.total}    sub={`${employees.active} active · ${employees.inactive} inactive`}    color={C.blue}   icon="👥" />
          <KpiCard title="Working Today"      value={attendance_today.present + attendance_today.wfh} sub={`${attendance_today.wfh} WFH · ${attendance_today.on_leave} on leave`} color={C.green}  icon="✅" />
          <KpiCard title="Active Projects"    value={projects.active}   sub={`${projects.delayed} delayed · ${projects.on_track} on track`}     color={projects.delayed > 0 ? C.red : C.blue} icon="📂" />
          <KpiCard title="Tender Win Rate"    value={`${sales.win_ratio}%`} sub={`${sales.won} won · ${sales.lost} lost`}                       color={sales.win_ratio >= 50 ? C.green : C.orange} icon="🏆" />
          <KpiCard title="Pipeline Value"     value={fmtCr(sales.pipeline_value)} sub={`Won: ${fmtCr(sales.won_value)}`}                        color={C.purple}  icon="💰" />
          <KpiCard title="Asset Utilization"  value={`${assets.utilization_pct}%`} sub={`${assets.assigned} assigned / ${assets.total} total`} color={C.teal}   icon="💻" />
          <KpiCard title="Ticket Resolution"  value={`${tickets.resolution_rate}%`} sub={`Avg ${tickets.avg_resolution_hours}h · ${tickets.escalated} escalated`} color={tickets.escalated > 0 ? C.red : C.green} icon="🎫" />
          {!isCTO && <KpiCard title="Claims Submitted" value={claims.submitted} sub={`${fmtCr(claims.total_value)} total value · ${claims.paid} paid`} color={C.orange} icon="🧾" />}
          <KpiCard title="Avg Performance"    value={performance.avg_rating || '—'} sub={`${performance.top_performers} top · ${performance.low_performers} needs improvement`} color={C.indigo} icon="⭐" />
        </div>
      </div>

      {/* ── Alerts sidebar layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '1.5rem', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

          {/* ── 2. Workforce ── */}
          {show('workforce') && (
            <div>
              <SectionHeader icon="👥" title="Workforce & Attendance" sub={`${employees.new_joiners_this_month} new joiners · ${employees.exits_this_month} exits this month · ${employees.attrition_rate}% quarterly attrition`} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

                {/* Attendance today */}
                <ChartCard title="Attendance Today" sub={`${attendance_today.total_expected} expected`} height={220}>
                  {attPie.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={attPie} cx="50%" cy="50%" innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3}>
                          {attPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No attendance records today" />}
                </ChartCard>

                {/* Headcount trend */}
                <ChartCard title="Headcount Trend — Last 6 Months" height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={employees.headcount_trend} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <defs>
                        <linearGradient id="hcGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.blue} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={C.blue} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="month" tick={TICK_STYLE} />
                      <YAxis tick={TICK_STYLE} allowDecimals={false} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Area dataKey="headcount" stroke={C.blue} strokeWidth={2.5} fill="url(#hcGrad)" name="Headcount" dot={{ r: 3 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Department distribution */}
                <ChartCard title="Workforce by Department" height={Math.max(200, employees.by_department.length * 36)} span={2}>
                  {employees.by_department.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={employees.by_department} layout="vertical" margin={{ top: 0, right: 30, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} horizontal={false} />
                        <XAxis type="number" tick={TICK_STYLE} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={120} tick={TICK_STYLE} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" name="Employees" radius={[0, 4, 4, 0]}>
                          {employees.by_department.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No department data" />}
                </ChartCard>

                {/* Attendance trend */}
                <ChartCard title="Attendance Trend — Last 6 Months" height={220} span={2}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attendance_trend} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="month" tick={TICK_STYLE} />
                      <YAxis tick={TICK_STYLE} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="present"  fill={C.green}  name="Present"  radius={[3,3,0,0]} stackId="a" />
                      <Bar dataKey="wfh"      fill={C.blue}   name="WFH"      radius={[0,0,0,0]} stackId="a" />
                      <Bar dataKey="on_leave" fill={C.yellow} name="On Leave" radius={[3,3,0,0]} stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          )}

          {/* ── 3. Sales & Tender Pipeline ── */}
          {show('sales') && (
            <div>
              <SectionHeader icon="📈" title="Sales & Tender Pipeline" sub={`Weighted forecast: ${fmtCr(sales.weighted_pipeline)} · ${sales.upcoming_deadlines?.length || 0} deadline(s) this week`} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                <KpiCard title="Total Tenders"    value={sales.total_tenders}   sub={`${sales.active} active`}                color={C.blue}   icon="📋" />
                <KpiCard title="Pipeline Value"   value={fmtCr(sales.pipeline_value)} sub="Active tenders"                   color={C.purple} icon="💰" />
                <KpiCard title="Won Value"         value={fmtCr(sales.won_value)}  sub={`${sales.won} tenders won`}           color={C.green}  icon="✅" />
                <KpiCard title="Win Rate"          value={`${sales.win_ratio}%`}  sub={`${sales.lost} lost`}                 color={sales.win_ratio >= 50 ? C.green : C.orange} icon="🏆" />
                <KpiCard title="Weighted Forecast" value={fmtCr(sales.weighted_pipeline)} sub="Probability-adjusted"         color={C.indigo} icon="📊" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <ChartCard title="Pipeline by Stage" height={220}>
                  {stageData.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageData} margin={{ top: 4, right: 8, bottom: 28, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.textMuted }} angle={-20} textAnchor="end" interval={0} />
                        <YAxis tick={TICK_STYLE} allowDecimals={false} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" name="Tenders" radius={[4,4,0,0]}>
                          {stageData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No tender data" />}
                </ChartCard>

                <ChartCard title="Tenders by Portal" height={220}>
                  {sales.by_portal?.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sales.by_portal} layout="vertical" margin={{ top: 0, right: 24, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} horizontal={false} />
                        <XAxis type="number" tick={TICK_STYLE} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: theme.textMuted }} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" name="Tenders" radius={[0,4,4,0]}>
                          {sales.by_portal.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No portal data" />}
                </ChartCard>
              </div>

              {/* Upcoming deadlines */}
              {sales.upcoming_deadlines?.length > 0 && (
                <div style={{ marginTop: '1rem', background: '#FEF2F2', border: `1px solid #FCA5A5`, borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 700, color: C.red, margin: '0 0 0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    ⏰ Upcoming Tender Deadlines (Next 7 Days)
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {sales.upcoming_deadlines.map((d, i) => (
                      <span key={i} style={{ background: '#fff', border: `1px solid #FCA5A5`, borderRadius: '0.5rem', padding: '0.3rem 0.75rem', fontSize: '0.78rem', color: theme.text, fontWeight: 500 }}>
                        {d.title} <span style={{ color: C.red, fontWeight: 700 }}>· {d.deadline ? new Date(d.deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 4. Projects Health ── */}
          {show('projects') && (
            <div>
              <SectionHeader icon="📂" title="Projects Health" sub={`${projects.active} active · ${projects.delayed} delayed · ${projects.completed} completed`} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <ChartCard title="Project Status" height={220}>
                  {projPie.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={projPie} cx="50%" cy="50%" innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3}>
                          {projPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No project data" />}
                </ChartCard>

                <ChartCard title="Project Deliveries — Last 6 Months" height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={project_trend} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="month" tick={TICK_STYLE} />
                      <YAxis tick={TICK_STYLE} allowDecimals={false} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Line dataKey="completed" stroke={C.blue} strokeWidth={2.5} dot={{ r: 4, fill: C.blue }} name="Completed" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* Members per project & Weekly hours per project — horizontal bars, top 15 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginTop: '1.25rem' }}>
                <ChartCard title="Members per Project (Active)" height={360}>
                  {projects.members_per_project?.length ? (() => {
                    const data = [...projects.members_per_project].sort((a, b) => b.members - a.members).slice(0, 15);
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.border} horizontal={false} />
                          <XAxis type="number" tick={TICK_STYLE} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10, fill: theme.textMuted }} tickFormatter={v => v.length > 18 ? v.slice(0, 17) + '…' : v} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Bar dataKey="members" name="Members" radius={[0, 4, 4, 0]} barSize={14}>
                            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })() : <EmptyState text="No project member data" />}
                </ChartCard>

                <ChartCard title="Weekly Work Hours per Project" height={360}>
                  {projects.weekly_hours_per_project?.length ? (() => {
                    const data = [...projects.weekly_hours_per_project].sort((a, b) => b.hours - a.hours).slice(0, 15);
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.border} horizontal={false} />
                          <XAxis type="number" tick={TICK_STYLE} />
                          <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10, fill: theme.textMuted }} tickFormatter={v => v.length > 18 ? v.slice(0, 17) + '…' : v} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}h`, 'Hours']} />
                          <Bar dataKey="hours" name="Hours" radius={[0, 4, 4, 0]} barSize={14}>
                            {data.map((_, i) => <Cell key={i} fill={PALETTE[(i + 3) % PALETTE.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })() : <EmptyState text="No timesheet entries this week" />}
                </ChartCard>
              </div>

              {projects.delayed_names?.length > 0 && (
                <div style={{ marginTop: '1rem', background: '#FEF2F2', border: `1px solid #FCA5A5`, borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 700, color: C.red, margin: '0 0 0.5rem' }}>⚠️ Delayed Projects</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {projects.delayed_names.map((n, i) => (
                      <span key={i} style={{ background: '#fff', border: `1px solid #FCA5A5`, borderRadius: '0.4rem', padding: '0.25rem 0.65rem', fontSize: '0.78rem', color: theme.text }}>{n}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 5. IT Health ── */}
          {show('it') && (
            <div>
              <SectionHeader icon="🖥" title="IT Health — Assets & Tickets" sub={`${tickets.escalated} escalated · ${assets.in_repair} under repair · ${tickets.avg_resolution_hours}h avg resolution`} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                <KpiCard title="Open Tickets"    value={tickets.open + tickets.in_progress} sub={`${tickets.escalated} escalated`}        color={tickets.escalated > 0 ? C.red : C.orange} icon="🎫" />
                <KpiCard title="Resolution Rate" value={`${tickets.resolution_rate}%`}       sub={`Avg ${tickets.avg_resolution_hours}h`} color={tickets.resolution_rate >= 80 ? C.green : C.yellow} icon="✅" />
                <KpiCard title="Total Assets"    value={assets.total}                        sub={`${assets.assigned} assigned · ${assets.available} free`} color={C.teal}  icon="💻" />
                <KpiCard title="Asset Utilization" value={`${assets.utilization_pct}%`}     sub={`${assets.in_repair} under repair`}    color={C.blue}   icon="📊" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

                <ChartCard title="Asset Status Distribution" height={220}>
                  {assetPie.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={assetPie} cx="50%" cy="50%" innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3}>
                          {assetPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No asset data" />}
                </ChartCard>

                <ChartCard title="Assets by Type / Category" height={220}>
                  {assets.by_type?.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={assets.by_type} margin={{ top: 4, right: 8, bottom: 28, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.textMuted }} angle={-20} textAnchor="end" interval={0} />
                        <YAxis tick={TICK_STYLE} allowDecimals={false} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" name="Count" radius={[4,4,0,0]}>
                          {assets.by_type.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No asset category data" />}
                </ChartCard>

                <ChartCard title="Open Tickets by Priority" height={200} span={2}>
                  {tickets.by_priority?.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={tickets.by_priority} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                        <XAxis dataKey="name" tick={TICK_STYLE} />
                        <YAxis tick={TICK_STYLE} allowDecimals={false} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Bar dataKey="count" name="Tickets" radius={[4,4,0,0]}>
                          <Cell fill={C.gray} />
                          <Cell fill={C.blue} />
                          <Cell fill={C.orange} />
                          <Cell fill={C.red} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No open tickets" />}
                </ChartCard>
              </div>
            </div>
          )}

          {/* ── 6. Budget / Claims ── */}
          {show('claims') && (
            <div>
              <SectionHeader icon="💳" title="Expense Claims & Budget" sub={`Total: ${fmtCr(claims.total_value)} · Paid: ${fmtCr(claims.paid_value)}`} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
                <KpiCard title="Submitted"  value={claims.submitted} sub="Awaiting review" color={C.blue}   icon="📤" />
                <KpiCard title="Approved"   value={claims.approved}  sub="Ready to pay"   color={C.green}  icon="✅" />
                <KpiCard title="Paid"        value={claims.paid}      sub={fmtCr(claims.paid_value)} color={C.teal}  icon="💸" />
                <KpiCard title="Rejected"    value={claims.rejected}  sub="Not approved"  color={C.red}    icon="❌" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <ChartCard title="Claims Status Breakdown" height={220}>
                  {claimsPie.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={claimsPie} cx="50%" cy="50%" innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3}>
                          {claimsPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No claims data" />}
                </ChartCard>

                <ChartCard title="Monthly Claims Trend" height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={claims.monthly} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="month" tick={TICK_STYLE} />
                      <YAxis tick={TICK_STYLE} allowDecimals={false} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="submitted" fill={C.blue}  name="Submitted" radius={[3,3,0,0]} />
                      <Bar dataKey="approved"  fill={C.green} name="Approved"  radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          )}

          {/* ── 7. Recruitment ── */}
          {show('recruitment') && (
            <div>
              <SectionHeader icon="💼" title="Open Positions & Hiring Funnel" sub={`${recruitment.open_positions} open · ${recruitment.total_candidates} candidates · ${recruitment.hire_rate}% hire rate`} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
                <KpiCard title="Open Positions" value={recruitment.open_positions}   sub="Active requisitions"      color={C.blue}   icon="💼" />
                <KpiCard title="In Interview"   value={recruitment.in_interview}     sub="Currently scheduled"      color={C.yellow} icon="🗓" />
                <KpiCard title="Offer Stage"    value={recruitment.in_offer}         sub="Awaiting acceptance"      color={C.orange} icon="📄" />
                <KpiCard title="Hired (Total)"  value={recruitment.hired}            sub={`${recruitment.hire_rate}% conversion`} color={C.green}  icon="🎉" />
              </div>
              <ChartCard title="Hiring Funnel — Candidates by Stage" height={240}>
                {recruitment.funnel?.some(f => f.count > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={recruitment.funnel} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="stage" tick={TICK_STYLE} />
                      <YAxis tick={TICK_STYLE} allowDecimals={false} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Bar dataKey="count" name="Candidates" radius={[4,4,0,0]}>
                        {recruitment.funnel.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState icon="💼" text="No candidates in pipeline yet" />}
              </ChartCard>
            </div>
          )}

          {/* ── 8. Performance ── */}
          {show('performance') && (
            <div>
              <SectionHeader icon="⭐" title="Performance Overview" sub={`${performance.total_reviewed} reviews completed · Avg rating ${performance.avg_rating}/5`} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

                {/* Rating display */}
                <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.4rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 1.25rem' }}>Rating Summary</p>
                  <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '3.5rem', fontWeight: 900, lineHeight: 1, color: performance.avg_rating >= 4 ? C.green : performance.avg_rating >= 2.5 ? C.blue : C.red }}>
                      {performance.avg_rating || '—'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>Average Rating out of 5.0</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginTop: '0.5rem' }}>
                      {[1,2,3,4,5].map(s => (
                        <span key={s} style={{ fontSize: '1.2rem', color: s <= Math.round(performance.avg_rating) ? '#F59E0B' : theme.border }}>★</span>
                      ))}
                    </div>
                  </div>
                  {[
                    { label: 'Top Performers (≥4.0)',    count: performance.top_performers,    color: C.green },
                    { label: 'Average (2.5 – 4.0)',      count: performance.average_performers, color: C.blue },
                    { label: 'Needs Improvement (<2.5)', count: performance.low_performers,     color: C.red },
                  ].map((r, i) => (
                    <div key={i} style={{ marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                        <span style={{ fontSize: '0.78rem', color: theme.text }}>{r.label}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: r.color }}>{r.count}</span>
                      </div>
                      <div style={{ height: 7, background: theme.pageBg, borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${performance.total_reviewed > 0 ? (r.count / performance.total_reviewed) * 100 : 0}%`, height: '100%', background: r.color, borderRadius: 4, transition: 'width 0.6s' }} />
                      </div>
                    </div>
                  ))}
                  {performance.total_reviewed === 0 && <p style={{ fontSize: '0.82rem', color: theme.textMuted, textAlign: 'center', marginTop: '0.5rem' }}>No reviews submitted yet</p>}
                </div>

                {/* Performance bar chart */}
                <ChartCard title="Performance Distribution" height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { name: 'Top', count: performance.top_performers, fill: C.green },
                      { name: 'Average', count: performance.average_performers, fill: C.blue },
                      { name: 'Needs Improvement', count: performance.low_performers, fill: C.red },
                    ]} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.textMuted }} />
                      <YAxis tick={TICK_STYLE} allowDecimals={false} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Bar dataKey="count" name="Employees" radius={[4,4,0,0]}>
                        <Cell fill={C.green} />
                        <Cell fill={C.blue} />
                        <Cell fill={C.red} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          )}

        </div>{/* end main column */}

        {/* ── Sticky Alerts Panel ── */}
        <div style={{ position: 'sticky', top: 80, display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Quick stats */}
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1rem 1.1rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem' }}>📋 Live Pulse</p>
            {[
              { label: 'Present / WFH Today',  value: `${attendance_today.present} / ${attendance_today.wfh}`, color: C.green },
              { label: 'On Leave Today',         value: attendance_today.on_leave, color: C.yellow },
              { label: 'Leaves Pending',         value: leave_wfh?.pending_leaves ?? '—', color: C.orange },
              { label: 'WFH Pending',            value: leave_wfh?.pending_wfh ?? '—',    color: C.blue },
              { label: 'Tenders This Week',      value: sales.upcoming_deadlines?.length ?? 0, color: C.red },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: i < 4 ? `1px solid ${theme.border}` : 'none' }}>
                <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>{r.label}</span>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: r.color }}>{r.value}</span>
              </div>
            ))}
          </div>

          {/* Alerts */}
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1rem 1.1rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem', display: 'flex', alignItems: 'center' }}>
              🚨 Risk & Alerts
              <AlertBadge count={highAlerts.length} color={C.red} />
              <AlertBadge count={mediumAlerts.length} color={C.yellow} />
            </p>

            {highAlerts.length > 0 && (
              <>
                <Divider label="🔴 High" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', margin: '0.5rem 0' }}>
                  {highAlerts.map((a, i) => <AlertItem key={i} alert={a} />)}
                </div>
              </>
            )}
            {mediumAlerts.length > 0 && (
              <>
                <Divider label="🟡 Medium" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', margin: '0.5rem 0' }}>
                  {mediumAlerts.map((a, i) => <AlertItem key={i} alert={a} />)}
                </div>
              </>
            )}
            {greenAlerts.length > 0 && (
              <>
                <Divider label="🟢 Healthy" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', margin: '0.5rem 0' }}>
                  {greenAlerts.map((a, i) => <AlertItem key={i} alert={a} />)}
                </div>
              </>
            )}
            {alerts.length === 0 && <p style={{ fontSize: '0.82rem', color: theme.textMuted, textAlign: 'center', padding: '1rem 0' }}>No active alerts</p>}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <p style={{ textAlign: 'center', fontSize: '0.72rem', color: theme.textMuted, paddingBottom: '0.5rem' }}>
        BonTon Suite · Executive View · {lastRefresh?.toLocaleTimeString('en-IN')}
      </p>

    </div>
  );
}
